import { roundRect } from "../../../../utils/utils";
import {
    DEFAULT_CELL_LAYOUT, DEFAULT_CELL_STATE, DEFAULT_CELL_CONTENT, GRID_CELL_CLICKABLE_ELEMENTS, ENDPOINT_RADIUS, LABEL_DRAGPOINT
} from "./grid-cell.config";
import { PluginContainer } from "../plugin-container/plugin-container";
import {
    getContainerConfig, getContentConfig, getCellStyle, pointInRectangle
} from "./grid-cell.utils";
import { FabricRectangular } from "../FabricRectangular";
import { ConnectingLineHandler } from "../../modules/ConnectingLineHandler";
import { createNestedCircle } from "../components.utils";
import { DiagramUtils } from "../../../../diagram/diagram-assets/DiagramUtils";
import { AnchoredPluginsHandler } from "../../modules/AnchoredPluginsHandler";
import { getInstanceIDFromCanvasInstance } from "../../../../diagram/diagram-assets/diagram-positioning-state";

/**
 * @description GridCell instance. Each cell in the generated grid is represented by a GridCell instance
 */
export class GridCell extends FabricRectangular {
  content = { ...DEFAULT_CELL_CONTENT() };

  state = { ...DEFAULT_CELL_STATE() };

  layout = { ...DEFAULT_CELL_LAYOUT() };

  labelHandler = null;

  ctx;

  endpointRadius = ENDPOINT_RADIUS;

  connectingLineHandler;

  uuid = new DiagramUtils().generateUUID();

  initialLabel;

  anchoredPluginsHandler;

  newPlugin = null;

  contentData;

  customDrawingFuncs = [];

  constructor(layout, state, canvas, labelHandler) {
      super(layout);
      this.layout = { ...this.layout, ...layout };
      this.state = { ...this.state, ...state };
      this.ctx = canvas;
      this.labelHandler = labelHandler;
      this.connectingLineHandler = new ConnectingLineHandler(getInstanceIDFromCanvasInstance(this.ctx));
      this.anchoredPluginsHandler = new AnchoredPluginsHandler(getInstanceIDFromCanvasInstance(this.ctx));
      this.connectingLineHandler.registerCell(this.uuid, this.layout, this.state, this, this.ctx);
  }

  /**
   * @description Redraws the cell, content and emits events to connectingLineHandler to announce the cell has been redrawed
   * @param {Boolean} fromLineHandler  - indicates if the cell draw request came from connectingLineHandler
   * @param {Boolean} forceRedraw
   */
  draw(fromLineHandler, forceRedraw) {
      if (this.isVoid() && !forceRedraw) {
          return;
      }
      // Check to see if it was initially drawed, clear it and then proceed with a new drawing.
      if (this.state.drawed) {
          this.clearCell();
          if (this.hasDrawedContent()) this.getContentInstance().drawed = false;
          if (this.hasDrawedContainer()) this.getContainerInstance().drawed = false;
      }
      this.state.drawed = true;
      this.layout.styles = { ...this.layout.styles, ...getCellStyle(this.state, this.hasContent()) };
      roundRect(this.layout.x, this.layout.y, this.layout.width, this.layout.height, this.layout.radius, this.layout.styles, this.ctx);
      this.drawContents();
      if (this.connectingLineHandler.isRegistered(this.uuid) && !fromLineHandler) {
          this.connectingLineHandler.redrawRequest(this.uuid, this.labelHandler);
      }
      this.anchoredPluginsHandler.cellDrawing(this.uuid);
      if (this.customDrawingFuncs) {
          this.customDrawingFuncs.forEach((custom) => {
              custom.func(this);
          });
      }
  }

  /**
   * @description sets a custom drawing func
   * @param {Function} func- Custom drawing function to be called in the draw process
   */
  setCustomDrawingFunc(func) {
      this.customDrawingFuncs.push(func);
  }

  /**
   * @description clears a specific custom drawing func
   * @param {String} toClearName - the name of the func that needs to be cleared
   */
  clearSpecificCustomDrawingFunc(toClearName) {
      this.customDrawingFuncs = this.customDrawingFuncs.filter(custom => custom.name !== toClearName);
  }

  /**
   * @description clears all custom drawing functions
   */
  clearCustomDrawingFunc() {
      this.customDrawingFuncs = [];
  }

  /**
   * @Description - returns the size of the cell
   */
  getSize() {
      return { width: this.layout.width, height: this.layout.height };
  }

  /**
   * @Description - returns the position of the cell
   */
  getPosition() {
      return { x: this.layout.x, y: this.layout.y };
  }

  /**
  * @Description - Draws the content of the cell
  */
  drawContents() {
      if (this.hasContent()) {
          if (this.state.disabled) {
              // this.ctx.filter = 'grayscale(100%)';
              this.ctx.filter = "opacity(15%)";
          }
          this.drawPluginContainer();
          this.drawPlugin();
          this.ctx.filter = "none";

          this.drawLabel();
          this.ctx.lineWidth = 1;
      }
  }

  /**
    * @Description - Draws the plugin inside the cell
    */
  drawPlugin() {
      if (this.hasDrawedContent()) {
          const additionalOptions = this.labelHandler.getAdditionalOptions(this.getContentInstance().uuid);
          const disabled = this.state && this.state.disabled;
          this.getContentInstance().draw(additionalOptions, disabled);
          return;
      }
      const data = getContentConfig(this.content.config.content, this.layout, this.ctx);
      this.setContentInstance(new this.content.factory(data.options, data.ctx, this.contentData));
      this.getContentInstance().draw();
  }

  /**
      * @Description - Draws the plugin container inside the cell
      */
  drawPluginContainer() {
      const layoutContainer = getContainerConfig(this.content.config.container, this.layout);
      if (this.hasDrawedContainer()) {
          this.getContainerInstance().draw();
          return layoutContainer;
      }
      this.setContainerInstance(new PluginContainer(layoutContainer, { selected: this.selected }, this.ctx, {}));
      this.getContainerInstance().draw();
      return layoutContainer;
  }

  /**
  * @Description - Sets the initial label value
  * @param {String} value - the initial label value
  */
  setInitialLabel(value) {
      this.initialLabel = value;
  }

  /**
   * @description Sets a mark that a new plugin has been set
   * @param {Boolean} value
   */
  setNewPlugin(value) {
      this.newPlugin = value;
  }

  /**
   * @description Draws the label
   */
  drawLabel() {
      if (this.newPlugin) {
          this.labelHandler.addNewLabel(this, this.ctx);
          this.newPlugin = null;
      }

      const plugin = this.labelHandler.getCellLabel(this.contentData.uuid ? this.contentData.uuid : this.uuid, this.uuid);

      if (plugin) {
          this.ctx.textBaseline = "top";
          this.ctx.textAlign = "center";
          this.ctx.font = `${this.content.config.propertiesLabel.fontLabel}px`;
          this.ctx.fillStyle = this.state.selected ? "#256BDE" : "#444444";

          let widthOfPluginText = null; const scope = this;
          const textObject = this.labelHandler.getLabelWidth(this.ctx, plugin.label);
          // this.labelHandler.clearRectBeforeCreateLabel(this.ctx, plugin.label, plugin.labelPosition.x, plugin.labelPosition.y, null, textObject["secondLine"] ? "HAS_SECOND_LINE" : null);

          if (textObject.secondLine) {
              widthOfPluginText = textObject.labelWidth;
              this.labelHandler.createBackgroundLabelRectangle(plugin.labelPosition.x, plugin.labelPosition.y, widthOfPluginText.width, this.ctx, null, "HAS_SECOND_LINE");
              setTimeout(() => {
                  scope.ctx.fillText(textObject.firstLine, plugin.labelPosition.x,
                      plugin.labelPosition.y - parseInt(this.labelHandler.labelProperties.fontLabel) - parseInt(this.labelHandler.labelProperties.paddingLabelTopBottom) - (parseInt(this.labelHandler.labelProperties.paddingLabelTopBottom) / 2));
                  scope.ctx.fillText(textObject.secondLine, plugin.labelPosition.x, plugin.labelPosition.y - parseInt(this.labelHandler.labelProperties.paddingLabelTopBottom) / 2);
              }, 0);
          } else {
              widthOfPluginText = textObject.labelWidth;
              this.labelHandler.createBackgroundLabelRectangle(plugin.labelPosition.x, plugin.labelPosition.y, widthOfPluginText.width, this.ctx);
              setTimeout(() => {
                  scope.ctx.fillText(textObject.firstLine, plugin.labelPosition.x, plugin.labelPosition.y, scope.layout.width);
              }, 0);

              widthOfPluginText = textObject.labelWidth;
          }

          createNestedCircle(plugin.labelPosition.x - (widthOfPluginText.width / 2) - this.content.config.propertiesLabel.paddingLabelX - this.labelHandler.labelProperties.dragPointLabelRadius,
              plugin.labelPosition.y + (textObject.secondLine ? -(this.labelHandler.labelProperties.dragPointLabelRadius + this.labelHandler.labelProperties.fontLabel / 2) : parseInt(this.content.config.propertiesLabel.fontLabel) / 2),
              [this.content.config.propertiesLabel.dragPointLabelRadius], LABEL_DRAGPOINT, this.ctx);
      }
  }

  /**
   * @description clears the cell drawing
   */
  clearCell() {
      this.state.drawed = false;
      const lineWidth = this.layout.styles.lineWidth || 1;
      if (this.state.void) {
          return;
      }
      this.ctx.clearRect(this.layout.x - lineWidth, this.layout.y - lineWidth, this.layout.width + lineWidth * 2, this.layout.height + lineWidth * 2);
  }

  /**
   * @description Updates te cell layout, state
   * @param {object} layout
   * @param {object} state
   * @param {boolean} noLineHandlerEmission - inidicates if it should emit events to the lineHandler
   */
  update(layout, state, noLineHandlerEmission) {
      super.setPoints(layout);
      if (this.state.temporary && !state.temporary && !this.connectingLineHandler.isRegistered(this.uuid)) {
          this.connectingLineHandler.registerCell(this.uuid, this.layout, this.state, this, this.ctx);
      }
      if (layout) this.layout = { ...this.layout, ...layout };
      if (state) this.state = { ...this.state, ...state };

      if (this.hasDrawedContainer()) {
          const layout = getContainerConfig(this.content.config.container, this.layout);
          this.getContainerInstance().update(layout, {
              active: this.state.active, disabled: this.state.disabled, selected: this.state.selected, connectingLineHover: this.state.connectingLineHover
          });
      }
      if (this.hasDrawedContent()) {
          const data = getContentConfig(this.content.config.content, this.layout, this.ctx);
          this.getContentInstance().update(data.options);
      }
      if (!noLineHandlerEmission) {
          this.connectingLineHandler.updateCell(this.uuid, layout, state, this);
      }
  }

  /**
   * @descriptino checks to see what element inside the cell was clicked
   * @param {Object} pos  - mouse position to check
   */
  getClickedElement(pos) {
      const content = this.getContentInstance(); let
          insideContent;
      if (content) {
          insideContent = pointInRectangle({ ...content.position, ...content.size }, pos);
          if (insideContent) {
              return GRID_CELL_CLICKABLE_ELEMENTS.CONTENT;
          }
      }
  }

  /**
   * @description - Returns the content factory
   */
  getContentFactory() {
      return this.content.factory;
  }

  /**
   * @description -  updates the drag hover state status
   * @param {Boolean} dragHover
   */
  setDragHover(dragHover) {
      this.state.dragHover = dragHover;
  }

  /**
   * @description returns the drag hover state status
   */
  getDragHover() {
      return this.state.dragHover;
  }

  /**
   * @description sets the content to the cell and factory
   * @param {Object} content
   * @param {Object} data
   */
  setContent(content, data) {
      if (content.factory) this.content.factory = content.factory;
      this.contentData = data;
  }

  /**
   * @description Sets the container instance ref to the cell
   * @param {Object} instance - Container Plugin instance
   */
  setContainerInstance(instance) {
      this.content.instance.container = instance;
  }

  /**
   * @description Sets the plugin instance ref to the cell
   * @param {Object} instance - Plugin instance
   */
  setContentInstance(instance) {
      this.content.instance.content = instance;
  }

  /**
   * @description returns the temporary state value
   */
  getTemporary() {
      return this.state.temporary;
  }

  /**
   * @description sets the temporary state value
   */
  setTemporary(temp) {
      this.state.temporary = temp;
  }

  /**
   * @description Checks to see if the cell has content set
   * @returns { Boolean }
   */
  hasDrawedContent() {
      return !!this.content.instance.content;
  }

  /**
  * @description Checks to see if the cell has container instance set
  * @returns { Boolean }
  */
  hasDrawedContainer() {
      return !!this.content.instance.container;
  }

  /**
  * @description Returns the container instance reference
  * @returns { Object }
  */
  getContainerInstance() {
      return this.content.instance.container;
  }

  /**
 * @description Returns the plugin instance reference
 * @returns { Object }
 */
  getContentInstance() {
      return this.content.instance.content;
  }

  /**
 * @description Checks to see if is void. Vooid indicates if the cell is covered by a cell independent plugin
 * @returns { Object }
 */
  isVoid() {
      return this.state.void;
  }

  /**
   * @description Checks to see if there is a factory set
   * @returns {boolean}
   */
  hasContent() {
      return !!this.content.factory;
  }

  /**
   * @description Clears the cell content and sets the defaults
   */
  clearContent() {
      this.content.instance = { ...DEFAULT_CELL_CONTENT() };
      this.content.factory = null;
      this.contentData = null;
  }

  /**
   * @description Deletes this cells referencer from the connectingLineHandler instance
   */
  deleteCell() {
      this.connectingLineHandler.deleteCell(this.uuid, this.state, this.layout);
  }
}
